import { useQuery } from "@apollo/client";
import AssignmentLateOutlined from "@mui/icons-material/AssignmentLateOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import FactCheckOutlined from "@mui/icons-material/FactCheckOutlined";
import GavelOutlined from "@mui/icons-material/GavelOutlined";
import PhoneOutlined from "@mui/icons-material/PhoneOutlined";
import RateReviewOutlined from "@mui/icons-material/RateReviewOutlined";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link as MuiLink,
  Stack,
  Typography,
} from "@mui/material";
import { StatusChip } from "components/StatusChip";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { computeEpaCorrectionDeadline } from "encamp-shared/src/waste/epaCorrectionRequests";
import { describeRuleKey } from "encamp-shared/src/waste/fieldIssueRuleKeys";
import {
  DiscrepancyKind,
  ManifestComplianceEscalation,
  ResolutionStatus,
  WasteManifestSource,
} from "generated-graphql/graphql";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  getDiscrepancyStatus,
  getFieldLabel,
  getStatusLabel,
} from "routes/Customer/Waste/Manifests/DiscrepancyReviewDialog.helpers";
import { MANIFEST_BLOCK_REASON_LABELS } from "routes/Customer/Waste/Manifests/manifestBlockReasonDisplay";
import { SOURCE_LABEL } from "routes/Customer/Waste/Manifests/manifestSourceDisplay";
import { DRAWER_PARAM, toDrawerParam } from "routes/Staff/GeneratorStatusChanges/drawerDeepLink";
import { formatPeriodSpan } from "routes/Staff/GeneratorStatusChanges/periodFormat";

import {
  MANIFEST_REVIEW_DISCREPANCIES_QUERY,
  type ManifestReviewDiscrepancy,
  type ManifestReviewRow,
  assigneeLabel,
  isActiveDiscrepancy,
  isCustomerVisibleDiscrepancy,
} from "./api";

/** Staff-only fields the dialog needs in `mode: "staff"`. */
interface ManifestDetailStaffDetails {
  tenantName: string;
  facilityEpaId?: string | null;
  status: string;
  /** Why the manifest is blocked (non-null = status BLOCKED). */
  blockedReason?: ManifestReviewRow["blockedReason"];
  blockedNote?: string | null;
  assignee: ManifestReviewRow["assignee"];
  documentIds: string[];
  onAssign: () => void;
}

interface ManifestDetailDialogCommonProps {
  open: boolean;
  onClose: () => void;
  /** null when no manifest is selected. */
  processedManifestId: string | null;
  manifestTrackingNumber: string | null;
  facilityName?: string | null;
  /**
   * Customer read-only view: every non-dismissed discrepancy, at any status —
   * see isCustomerVisibleDiscrepancy. Defaults to the active (open) view; only
   * meaningful in `mode: "customer"`.
   */
  customerView?: boolean;
}

/**
 * Discriminated on `mode`: the `"customer"` variant is read-only (hides Tenant,
 * Assignee, document-review cards, and the Assign button) and forbids
 * `staffDetails`; the `"staff"` variant requires `staffDetails`. This makes the
 * staff fields compile-time mandatory in staff mode (no silent downgrade to the
 * customer view from a forgotten `staffDetails`).
 */
export type ManifestDetailDialogProps = ManifestDetailDialogCommonProps &
  ({ mode: "customer" } | { mode: "staff"; staffDetails: ManifestDetailStaffDetails });

// Friendly labels for the derived manifest status enum (DERIVED_STATUS_SQL /
// rankToStatus) so the dialog never shows the raw value.
const MANIFEST_STATUS_LABELS: Record<string, string> = {
  BLOCKED: "Blocked",
  NEW_VERSION: "New Version",
  PENDING_REVIEW: "No open discrepancies",
  UNRESOLVED: "Unresolved",
  OVERDUE_45: "Overdue (45+ days)",
  OVERDUE_60: "Overdue (60+ days)",
  PENDING: "Pending",
};

/** Why the manifest is blocked (reason + optional note); renders nothing when workable. */
function BlockedFields({ staffDetails }: { staffDetails: ManifestDetailStaffDetails }) {
  if (!staffDetails.blockedReason) return null;
  return (
    <>
      <Field
        label="Blocked reason"
        value={
          MANIFEST_BLOCK_REASON_LABELS[staffDetails.blockedReason] ?? staffDetails.blockedReason
        }
      />
      {staffDetails.blockedNote && <Field label="Blocked note" value={staffDetails.blockedNote} />}
    </>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Stack>
  );
}

/** A single source's value within a field discrepancy; hidden when that source has no value. */
function SourceValue({ label, value }: { label: string; value: string | null | undefined }) {
  if (value == null || value === "") return null;
  return (
    <Stack direction="row" spacing={1}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 76 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ wordBreak: "break-word" }}>
        {value}
      </Typography>
    </Stack>
  );
}

/**
 * One detail line item. `accent` drives the left border color, by STATUS: `warning` (orange)
 * for UNRESOLVED, `info` (blue) for PENDING and for document reviews awaiting review.
 */
function DetailCard({
  accent,
  title,
  chips,
  children,
}: {
  accent: "warning" | "info" | "success";
  title: string;
  chips?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <Box sx={{ borderLeft: 3, borderColor: `${accent}.main`, pl: 1.5, py: 0.75 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Typography variant="body2" fontWeight="medium" sx={{ wordBreak: "break-word" }}>
          {title}
        </Typography>
        {chips && (
          <Stack direction="row" spacing={0.5} flexShrink={0}>
            {chips}
          </Stack>
        )}
      </Stack>
      {children}
    </Box>
  );
}

// All @mui icons share this component type; reuse one to type the prop.
type ActionIcon = typeof ErrorOutline;

/** A titled action group: colored icon + label, then its cards. */
function ActionSection({
  Icon,
  color,
  label,
  children,
}: {
  Icon: ActionIcon;
  color: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Icon sx={{ fontSize: 18, color }} />
        <Typography variant="subtitle2" sx={{ color }}>
          {label}
        </Typography>
      </Stack>
      <Stack spacing={1}>{children}</Stack>
    </Stack>
  );
}

/**
 * Color by STATUS: green (success) = RESOLVED/IGNORED (the customer "done" view),
 * blue (info) = PENDING, orange (warning) = UNRESOLVED.
 */
function statusAccent(status: string): "warning" | "info" | "success" {
  if (status === "RESOLVED" || status === "IGNORED") return "success";
  if (status === "PENDING") return "info";
  return "warning";
}

/** Human phrasing for which side was corrected when a discrepancy was resolved. */
function resolutionPathLabel(path: ManifestReviewDiscrepancy["resolutionPath"]): string {
  if (path === "EMANIFEST") return "Corrected in e-Manifest";
  if (path === "VENDOR_DATA") return "Corrected in vendor data";
  if (path === "NEVER_IN_EMANIFEST") return "Will never be in e-Manifest";
  if (path === "MANUAL_ENTRY") return "Reviewer-chosen value";
  // RESOLVED with a null path means the corrected side couldn't be attributed.
  return "Resolved";
}

/**
 * Resolution footer for a resolved discrepancy: which side was corrected, when,
 * and any note. Shows the two source values above still, so the reader sees both
 * what the values were and how it was closed. Renders nothing until resolved.
 */
function ResolutionDetail({ discrepancy }: { discrepancy: ManifestReviewDiscrepancy }) {
  // Only RESOLVED rows get a resolution footer. IGNORED (dismissed) rows also
  // carry a resolvedAt but no resolutionPath, so a `!resolvedAt` check would let
  // them through and print "Resolved" under an IGNORED status chip.
  if (discrepancy.status !== "RESOLVED") return null;
  const when = discrepancy.resolvedAt ? getUtcDisplayDate(discrepancy.resolvedAt, "med") : null;
  return (
    <Stack sx={{ mt: 0.75 }}>
      <Typography variant="caption" sx={{ color: "success.main", fontWeight: 600 }}>
        {resolutionPathLabel(discrepancy.resolutionPath)}
        {when ? ` · ${when}` : ""}
      </Typography>
      {discrepancy.resolutionNote && (
        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-word" }}>
          {discrepancy.resolutionNote}
        </Typography>
      )}
    </Stack>
  );
}

/**
 * Request line for an EPA correction card: when the regulator asked, who asked,
 * and the 30-day response deadline. Only EPA-correction rows carry the linked
 * WasteManifestCorrectionRequest; renders nothing for every other kind.
 */
function EpaCorrectionDetail({ discrepancy }: { discrepancy: ManifestReviewDiscrepancy }) {
  const wmcr = discrepancy.wasteManifestCorrectionRequest;
  if (!wmcr) return null;
  const requested = new Date(wmcr.requestDate);
  if (Number.isNaN(requested.getTime())) return null;
  const deadline = computeEpaCorrectionDeadline(requested);
  return (
    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
      Requested {getUtcDisplayDate(requested, "med")} by {wmcr.initiatorRole} · response due{" "}
      {getUtcDisplayDate(deadline, "med")}
    </Typography>
  );
}

/**
 * Card title: a FIELD_ISSUE row names the validity rule that fired (ruleKey),
 * not a field — the same label the customer Data Quality Issues pane uses.
 * Every other kind titles by field.
 */
function discrepancyCardTitle(discrepancy: ManifestReviewDiscrepancy): string {
  if (discrepancy.kind === DiscrepancyKind.FieldIssue) {
    return discrepancy.ruleKey ? describeRuleKey(discrepancy.ruleKey) : "Data quality issue";
  }
  return getFieldLabel(discrepancy.field);
}

/** Links to the generator status findings resolved by, or waiting on, this correction. Staff only. */
function CitingFindings({ discrepancy }: { discrepancy: ManifestReviewDiscrepancy }) {
  const findings = discrepancy.citedByGeneratorStatusFindings;
  if (findings.length === 0) return null;
  return (
    <Stack sx={{ mt: 0.75 }}>
      {findings.map((finding) => (
        <Typography key={toDrawerParam(finding)} variant="caption">
          Cited by generator status finding{" "}
          <MuiLink
            component={Link}
            to={`/staff/generator-status-changes?${DRAWER_PARAM}=${toDrawerParam(finding)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {formatPeriodSpan(finding)}
          </MuiLink>{" "}
          ({finding.status === ResolutionStatus.Pending ? "awaiting correction" : "resolved"})
        </Typography>
      ))}
    </Stack>
  );
}

/** One discrepancy's card (field label, severity/status chips, source values, resolution). */
function DiscrepancyDetailCard({ discrepancy }: { discrepancy: ManifestReviewDiscrepancy }) {
  const accent = statusAccent(discrepancy.status);
  return (
    <DetailCard
      accent={accent}
      title={discrepancyCardTitle(discrepancy)}
      chips={
        <>
          <StatusChip label={discrepancy.severity} color="default" variant="outlined" />
          {/* Through the shared label map, so this read-only card names a state
              the same way the review dialog's chips do. */}
          <StatusChip
            label={getStatusLabel(getDiscrepancyStatus(discrepancy))}
            color={accent}
            variant="outlined"
          />
        </>
      }
    >
      <Stack sx={{ mt: 0.5 }}>
        <SourceValue label="e-Manifest" value={discrepancy.eManifestValue} />
        <SourceValue label="Vendor" value={discrepancy.vendorValue} />
        <SourceValue
          label={SOURCE_LABEL[WasteManifestSource.ManifestDocument]}
          value={discrepancy.manifestDocumentValue}
        />
      </Stack>
      <EpaCorrectionDetail discrepancy={discrepancy} />
      <ResolutionDetail discrepancy={discrepancy} />
      <CitingFindings discrepancy={discrepancy} />
    </DetailCard>
  );
}

/**
 * "Data quality issues" section: open FIELD_ISSUE rows, one card each. A
 * standalone component (rather than an inline guard in ManifestDetailDialog)
 * so its own render decision doesn't add another branch to that function.
 */
function DataQualityIssuesSection({ items }: { items: ManifestReviewDiscrepancy[] }) {
  if (items.length === 0) return null;
  return (
    <ActionSection Icon={FactCheckOutlined} color="warning.main" label="Data quality issues">
      {items.map((d) => (
        <DiscrepancyDetailCard key={d.id} discrepancy={d} />
      ))}
    </ActionSection>
  );
}

export function ManifestDetailDialog(props: ManifestDetailDialogProps) {
  const { open, onClose, processedManifestId, manifestTrackingNumber, facilityName, customerView } =
    props;
  // The union guarantees staffDetails is present iff mode === "staff", so this
  // narrows without a cast. `isStaffMode` then drives the staff-only sections;
  // TS's aliased-condition narrowing keeps `staffDetails.x` accesses null-safe.
  const staffDetails = props.mode === "staff" ? props.staffDetails : null;

  const { data, loading } = useQuery(MANIFEST_REVIEW_DISCREPANCIES_QUERY, {
    variables: { processedManifestId: processedManifestId ?? "" },
    skip: !open || !processedManifestId,
  });

  const { featureFlags } = useFeatureFlags();
  // EPA-correction rows are hidden when their secondary flag is off, matching the
  // customer drawer (ManifestDetailSection) and the manifest-list tally — so the
  // modal never surfaces a feature the rest of the flow intentionally hides.
  const showEpaCorrections = !!featureFlags[FeatureFlagKey.WasteEpaCorrectionDiscrepancies];

  const isStaffMode = staffDetails != null;
  // Customers see every non-dismissed discrepancy; everyone else sees the open
  // (active) set. Dismissed non-discrepancies (IGNORED) render for no one.
  // customerView is only passed in customer mode (isStaffMode false).
  const shown = (data?.manifestDiscrepancies ?? []).filter(
    customerView ? isCustomerVisibleDiscrepancy : isActiveDiscrepancy
  );
  const encamp = shown.filter((d) => d.kind === DiscrepancyKind.FieldDiscrepancy);
  // EPA correction requests are regulator-driven, so this section renders for
  // both staff and customers whenever the EPA-correction flag is on — keeping the
  // per-manifest view in step with the Vendor Stats correction rollup. Only
  // missing-e-Manifest compliance (below) stays staff-only.
  const epa = showEpaCorrections
    ? shown.filter((d) => d.kind === DiscrepancyKind.EpaCorrectionRequest)
    : [];
  // Data Quality Issues (FIELD_ISSUE): the customer discrepancy dialog already
  // shows these to customers, ungated by staff mode, so this section renders
  // for both modes too — sourced from the same isActiveDiscrepancy /
  // isCustomerVisibleDiscrepancy filter already applied to `shown`.
  const dataQuality = shown.filter((d) => d.kind === DiscrepancyKind.FieldIssue);
  const missing = customerView
    ? []
    : shown.filter((d) => d.kind === DiscrepancyKind.EmanifestMissing);
  const vendorContact = missing.filter(
    (d) => d.escalation === ManifestComplianceEscalation.Overdue_45
  );
  const exceptionReport = missing.filter(
    (d) => d.escalation === ManifestComplianceEscalation.Overdue_60
  );
  // A missing-e-Manifest not yet escalated to a 45/60-day action is still an open
  // item, so surface it under "Manifest missing" for staff too — otherwise staff
  // would be shown "No open discrepancies" while one is active. At 45/60 days staff
  // instead see it as the Vendor contact / Exception report action. Customers always
  // see every missing row under "Manifest missing", regardless of escalation.
  const staffMissing = missing.filter(
    (d) =>
      d.escalation !== ManifestComplianceEscalation.Overdue_45 &&
      d.escalation !== ManifestComplianceEscalation.Overdue_60
  );
  const manifestMissingItems = isStaffMode ? staffMissing : missing;
  const documentCount = isStaffMode ? staffDetails.documentIds.length : 0;
  const documentPending = isStaffMode && staffDetails.assignee != null;
  // Count exactly the sections that can render in this mode so the empty state and
  // the rendered section list never disagree.
  const visibleCount = isStaffMode
    ? documentCount +
      encamp.length +
      epa.length +
      dataQuality.length +
      vendorContact.length +
      exceptionReport.length +
      manifestMissingItems.length
    : encamp.length + epa.length + dataQuality.length + manifestMissingItems.length;
  const hasNoDetails = !loading && visibleCount === 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {manifestTrackingNumber ? `Manifest ${manifestTrackingNumber}` : "Manifest"}
      </DialogTitle>
      <DialogContent dividers>
        {processedManifestId && (
          <Stack spacing={1}>
            {isStaffMode && <Field label="Tenant" value={staffDetails.tenantName} />}
            <Field label="Facility" value={facilityName ?? staffDetails?.facilityEpaId ?? "—"} />
            {isStaffMode && (
              <Field
                label="Status"
                value={MANIFEST_STATUS_LABELS[staffDetails.status] ?? staffDetails.status}
              />
            )}
            {isStaffMode && <BlockedFields staffDetails={staffDetails} />}
            {isStaffMode && <Field label="Assignee" value={assigneeLabel(staffDetails.assignee)} />}

            <Divider sx={{ mt: 1 }} />
            <Typography variant="subtitle2" color="text.secondary">
              Discrepancy details
            </Typography>

            {loading && (
              <Stack alignItems="center" py={2}>
                <CircularProgress size={20} />
              </Stack>
            )}

            {hasNoDetails && (
              <Typography variant="body2" color="text.secondary">
                {customerView ? "No discrepancies on this manifest." : "No open discrepancies."}
              </Typography>
            )}

            {/* The list scrolls within a bounded height so a manifest with many discrepancies
                never pushes the dialog past the viewport. */}
            {!loading && visibleCount > 0 && (
              <Stack spacing={1.5} sx={{ maxHeight: 360, overflowY: "auto", pr: 0.5, pb: 1.5 }}>
                {isStaffMode && documentCount > 0 && (
                  <ActionSection
                    Icon={RateReviewOutlined}
                    color="info.main"
                    label="Document review"
                  >
                    <DetailCard
                      accent={documentPending ? "info" : "warning"}
                      title={
                        documentCount === 1
                          ? "Document review"
                          : `Document reviews (${documentCount})`
                      }
                      chips={
                        <StatusChip
                          label={documentPending ? "PENDING" : "NEEDS REVIEW"}
                          color={documentPending ? "info" : "warning"}
                          variant="outlined"
                        />
                      }
                    >
                      <Typography variant="caption" color="text.secondary">
                        AI-ingested document awaiting review.
                      </Typography>
                    </DetailCard>
                  </ActionSection>
                )}

                {encamp.length > 0 && (
                  <ActionSection
                    Icon={ErrorOutline}
                    color="warning.main"
                    label="Encamp discrepancies"
                  >
                    {encamp.map((d) => (
                      <DiscrepancyDetailCard key={d.id} discrepancy={d} />
                    ))}
                  </ActionSection>
                )}

                {epa.length > 0 && (
                  <ActionSection Icon={GavelOutlined} color="error.main" label="EPA corrections">
                    {epa.map((d) => (
                      <DiscrepancyDetailCard key={d.id} discrepancy={d} />
                    ))}
                  </ActionSection>
                )}

                <DataQualityIssuesSection items={dataQuality} />

                {isStaffMode && vendorContact.length > 0 && (
                  <ActionSection Icon={PhoneOutlined} color="success.main" label="Vendor contact">
                    {vendorContact.map((d) => (
                      <DiscrepancyDetailCard key={d.id} discrepancy={d} />
                    ))}
                  </ActionSection>
                )}

                {isStaffMode && exceptionReport.length > 0 && (
                  <ActionSection
                    Icon={AssignmentLateOutlined}
                    color="#8e24aa"
                    label="Exception report"
                  >
                    {exceptionReport.map((d) => (
                      <DiscrepancyDetailCard key={d.id} discrepancy={d} />
                    ))}
                  </ActionSection>
                )}

                {manifestMissingItems.length > 0 && (
                  <ActionSection
                    Icon={AssignmentLateOutlined}
                    color="info.main"
                    label="Manifest missing"
                  >
                    {manifestMissingItems.map((d) => (
                      <DiscrepancyDetailCard key={d.id} discrepancy={d} />
                    ))}
                  </ActionSection>
                )}
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {isStaffMode ? (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={staffDetails.onAssign}
              disabled={!processedManifestId}
            >
              {staffDetails.assignee ? "Reassign" : "Assign"}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
